import {
  ActivityDefinitionPdfTemplate,
  attr,
  formGroupTemplate,
  html,
  PdfFieldMapping,
  printExpression,
  render,
  sanitizeFilename,
  uuidToBase58
} from '@cumu/shared';
import { target, targets } from '@github/catalyst';
import type { Attachment } from '@github/file-attachment-element';
import { uploadAttachments } from '../attachments';
import type { FormBehaviorElement } from '../forms/behavior-element';
import { busySubmitButtons } from '../forms/utilities';
import { getLocale } from '../locale';
import { mergePdfs } from '../pdf';
import { zipFiles } from '../zip';
import { controller } from './controller';
import { dispatchChange, scrollIntoViewIfNeeded } from './util';

@controller('pdf-template-list')
export class PdfTemplateListElement extends HTMLElement {
  @target declare fileInput: HTMLInputElement;

  async fileAttachmentAccepted(
    event: CustomEvent<{ attachments: Attachment[] }>
  ) {
    const uploadHref = this.getAttribute('upload-href');
    if (!uploadHref) {
      throw new Error('Missing upload href');
    }
    const uploadUrl = new URL(uploadHref, location.origin);
    const locale = getLocale();
    const attachments = event.detail.attachments;
    try {
      this.fileInput.disabled = true;
      this.fileInput.labels?.forEach(label => label.classList.add('btn-busy'));
      await uploadAttachments({
        locale,
        attachments,
        uploadUrl,
        attachmentSaved: ({ id }) => {
          location.href = `${location.origin}${location.pathname}/${uuidToBase58(id!)}`;
        }
      });
    } finally {
      this.fileInput.disabled = false;
      this.fileInput.labels?.forEach(label =>
        label.classList.remove('btn-busy')
      );
    }
  }
}

function breakAcroFieldName(name: string) {
  return name
    .split(/\]\./g)
    .map((x, i) => (i === 0 ? x : [html`]<wbr />.${x}`]))
    .flat();
}
interface PDFMetadata {
  info: PDFInfo;
  metadata?: {};
}

interface PDFInfo {
  Title?: string;
  Author?: string;
  Subject?: string;
  Keywords?: string;
  Creator?: string;
  Producer?: string;
  CreationDate?: string;
  ModDate?: string;
  Trapped?: string;
  [key: string]: any; // Other possible entries
}

interface AcroFormField {
  id: string;
  name: string;
  type: string;
  value?: string;
  defaultValue?: string;
  exportValues?: string;
  editable?: boolean;
  hidden?: boolean;
  multiline?: boolean;
  password?: boolean;
  charLimit?: number;
  comb?: boolean;
  rect?: [number, number, number, number];
  page?: number;
  rotation?: number;
  strokeColor?: RGBColor | null;
  fillColor?: RGBColor | null;
  actions?: any; // Define more strictly if needed
  kidIds?: string[];
}

interface RGBColor {
  0: number;
  1: number;
  2: number;
}

type FieldMap = Record<string, AcroFormField[]>; // Keyed by field name

interface AnnotationStorageEntry {
  value?: string | boolean | number;
}

interface AnnotationStorage {
  getAll(): Record<string, AnnotationStorageEntry>;
  getValue(
    key: string,
    defaultValue: Partial<AnnotationStorageEntry>
  ): AnnotationStorageEntry | undefined;
  setValue(key: string, value: AnnotationStorageEntry): void;
  remove(key: string): void;
  reset(): void;
  size: number;
  onSetModified: (() => void) | null;
  onResetModified: (() => void) | null;
  onAnnotationEditor: (() => void) | null;
}
interface PDFDocumentProxy {
  getMetadata: () => Promise<PDFMetadata>;
  getFieldObjects: () => Promise<FieldMap | null>;
  numPages: number;
  annotationStorage: AnnotationStorage;
}

interface PDFViewer {
  scrollPageIntoView: (options: {
    pageNumber: number;
    destArray?: any[];
    ignoreDestinationZoom?: boolean;
  }) => void;
  getPageView: (pageNumber: number) => {
    viewport: {
      convertToViewportPoint: (
        x: number,
        y: number
      ) => { x: number; y: number };
    };
    renderingState: number;
  };
}

interface PDFViewerApplication {
  initializedPromise: Promise<void>;
  pdfDocument: PDFDocumentProxy;
  appConfig: {
    mainContainer: HTMLElement;
    viewerContainer: HTMLElement;
    toolbar: {
      pageNumber: HTMLInputElement;
    };
    secondaryToolbar: {
      toolbar: HTMLElement;
      openFileButton: HTMLButtonElement;
    };
  };
  eventBus: {
    on: (
      event: string,
      callback: () => void,
      options?: { once?: boolean; signal?: AbortSignal }
    ) => void;
  };
  pdfViewer: PDFViewer;
  preferences: {
    set: (key: string, value: any) => void;
  };
  downloadOrSave(): Promise<void>;
  downloadManager: {
    download(
      data: Uint8Array<ArrayBufferLike>,
      url: string,
      filename: string
    ): void;
  };
}

abstract class PDFViewerBase extends HTMLElement {
  @target declare iframe: HTMLIFrameElement;
  pdfLoaded = Promise.withResolvers<PDFViewerApplication>();
  viewerApplication: PDFViewerApplication | undefined;
  detachedController: AbortController | undefined;

  abstract onAnnotationFieldClick?(
    viewerApplication: PDFViewerApplication,
    target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    event: Event
  ): void;

  abstract onAnnotationFieldFocus?(
    viewerApplication: PDFViewerApplication,
    target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    event: Event
  ): void;

  abstract onAnnotationFieldChange?(
    viewerApplication: PDFViewerApplication,
    target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    event: Event
  ): void;

  abstract onCtrlS?(viewerApplication: PDFViewerApplication): void;

  abstract onAnnotationLayerRendered?(
    viewerApplication: PDFViewerApplication
  ): void;

  abstract onPdfLoaded?(viewerApplication: PDFViewerApplication): void;

  connectedCallback() {
    this.detachedController = new AbortController();
  }

  disconnectedCallback() {
    this.detachedController?.abort();
  }

  async iframeLoaded() {
    const iframe = this.iframe;
    const viewerWindow = iframe.contentWindow;
    if (!viewerWindow) {
      throw new Error('Expected iframe contentWindow');
    }

    const viewerApplication = (this.viewerApplication = (viewerWindow as any)
      .PDFViewerApplication as PDFViewerApplication);

    await this.viewerApplication.initializedPromise;

    if (!this.hasAttribute('restore-position')) {
      // Prevent remembering the last page viewed.
      viewerApplication.preferences.set('viewOnLoad', 1); // 1 = initial position
      viewerApplication.preferences.set('defaultZoomValue', 'auto');
      viewerApplication.preferences.set('sidebarViewOnLoad', 0); // 0 = none, 1 = thumbnails, 2 = outline, 3 = attachments
      viewerApplication.preferences.set('scrollModeOnLoad', 0); // 0 = vertical scrolling
      viewerApplication.preferences.set('spreadModeOnLoad', 0); // 0 = no spreads
    }
    viewerApplication.preferences.set('enableSignatureEditor', true);

    // Prevent drop from updating the PDF document.
    viewerApplication.appConfig.mainContainer.addEventListener(
      'drop',
      event => {
        event.preventDefault();
        event.stopPropagation();
        console.log('STOPPED DROP');
      },
      { capture: true, signal: this.detachedController?.signal }
    );

    // Remove menu item which opens another PDF file.
    // TODO: remove the whole right menu? (secondaryToolbar.toolbar).
    const openFileMenuItem =
      viewerApplication.appConfig.secondaryToolbar.openFileButton;
    if (
      openFileMenuItem?.nextElementSibling?.matches(
        '.horizontalToolbarSeparator'
      )
    ) {
      openFileMenuItem.nextElementSibling.toggleAttribute('hidden', true);
    }
    openFileMenuItem?.toggleAttribute('hidden', true);

    // Field events
    viewerApplication.appConfig.viewerContainer.addEventListener(
      'click',
      this.handleAnnotationClick,
      { signal: this.detachedController?.signal }
    );
    viewerApplication.appConfig.viewerContainer.addEventListener(
      'focus',
      this.handleAnnotationFocus,
      {
        capture: true,
        signal: this.detachedController?.signal
      }
    );
    viewerApplication.appConfig.viewerContainer.addEventListener(
      'change',
      this.handleAnnotationChange,
      {
        signal: this.detachedController?.signal
      }
    );

    this.iframe.contentWindow?.window.addEventListener(
      'keydown',
      this.handleKeydown,
      {
        capture: true,
        signal: this.detachedController?.signal
      }
    );

    viewerApplication.eventBus.on(
      'annotationlayerrendered',
      () => this.onAnnotationLayerRendered?.(viewerApplication),
      {
        signal: this.detachedController?.signal
      }
    );

    await Promise.all([
      new Promise<void>(resolve =>
        viewerApplication.eventBus.on('documentloaded', resolve, {
          once: true,
          signal: this.detachedController?.signal
        })
      )
    ]);

    // Prevent edits to PDF from interrupting beforeunload event.
    viewerApplication.pdfDocument.annotationStorage.onSetModified = null;
    viewerApplication.pdfDocument.annotationStorage.onResetModified = null;
    viewerApplication.pdfDocument.annotationStorage.onAnnotationEditor = null;

    this.pdfLoaded.resolve(viewerApplication);
    this.onPdfLoaded?.(viewerApplication);
  }

  isAnnotationField(
    target: EventTarget | null
  ): target is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement {
    return (
      target &&
      'matches' in target &&
      typeof target.matches === 'function' &&
      target.matches(
        'section[data-annotation-id] [data-element-id]:is(input, select, textarea)'
      )
    );
  }

  handleAnnotationClick = async (event: Event) => {
    const target = event.target;
    if (!this.isAnnotationField(target) || !this.viewerApplication) {
      return;
    }

    // Make radios uncheckable.
    if (
      target.type === 'radio' &&
      target instanceof this.iframe.contentWindow!.window.HTMLInputElement &&
      target.checked
    ) {
      // Click happens before the change event.
      // Wait for the change event to fire, if it does, we do not need to clear the radio because it just became checked.
      const timeout = setTimeout(() => {
        target.checked = false;
        dispatchChange(target);
      });
      target.addEventListener('change', () => clearTimeout(timeout), {
        once: true,
        capture: true
      });
    }

    this.onAnnotationFieldClick?.(this.viewerApplication, target, event);
  };

  handleAnnotationFocus = async (event: Event) => {
    const target = event.target;
    if (!this.isAnnotationField(target) || !this.viewerApplication) {
      return;
    }
    this.onAnnotationFieldFocus?.(this.viewerApplication, target, event);
  };

  handleAnnotationChange = (event: Event) => {
    const target = event.target;
    if (!this.isAnnotationField(target) || !this.viewerApplication) {
      return;
    }
    this.onAnnotationFieldChange?.(this.viewerApplication, target, event);
  };

  handleKeydown = (event: KeyboardEvent) => {
    if (
      !(event.ctrlKey || event.metaKey) ||
      event.shiftKey ||
      event.altKey ||
      event.key !== 's' ||
      event.defaultPrevented ||
      !this.viewerApplication
    ) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation(); // Prevents the default save dialog from opening.
    const target = event.target;
    if (this.isAnnotationField(target)) {
      target.blur();
      target.focus();
    }
    this.onCtrlS?.(this.viewerApplication);
  };
}

@controller('pdf-template')
export class PdfTemplateElement extends PDFViewerBase {
  @target declare form: HTMLFormElement;
  @target declare titleInput: HTMLInputElement;
  @target declare startPageInput: HTMLInputElement;
  @target declare endPageInput: HTMLInputElement;
  @target declare noFieldsFlash: HTMLElement;
  @target declare fieldsScript: HTMLScriptElement;
  acroForm: FieldMap = {};
  fieldsById: Record<string, AcroFormField> = {};
  fieldOutlineTimeout = 0;
  removeFieldOutline = () => {};
  viewerOutlineTimeout = 0;
  removeViewerOutline = () => {};

  async connectedCallback() {
    super.connectedCallback();
    // Fix nested scroll issues.
    addEventListener('click', () => this.fixScrollTop('click event'));
    addEventListener('focus', () => this.fixScrollTop('focus event'), {
      capture: true
    });
  }

  async onPdfLoaded(viewerApplication: PDFViewerApplication) {
    // If new, set title and end page.
    if (this.form.querySelector('form-behavior[new]')) {
      const { info } = await viewerApplication.pdfDocument.getMetadata();
      this.titleInput.value = this.titleInput.value || info.Title || '';
      this.endPageInput.value =
        viewerApplication.pdfDocument.numPages.toString();
      (this.form.firstElementChild as FormBehaviorElement).acceptChanges();
    }

    // Set max page numbers.
    this.startPageInput.max = viewerApplication.pdfDocument.numPages.toString();
    this.endPageInput.max = viewerApplication.pdfDocument.numPages.toString();

    // Get form.
    const acroForm = await viewerApplication.pdfDocument.getFieldObjects();

    // If there are no fields, let them know, and return.
    if (
      !acroForm ||
      !Object.values(acroForm).some(fields =>
        fields.some(
          field =>
            ['text', 'checkbox', 'radiobutton'].includes(field.type) &&
            field.editable !== false &&
            field.hidden !== true
        )
      )
    ) {
      this.noFieldsFlash.hidden = false;
      return;
    }

    this.acroForm = acroForm;

    this.fieldsById = Object.values(acroForm)
      .flat()
      .reduce(
        (acc, field) => {
          acc[field.id] = field;
          return acc;
        },
        {} as Record<string, AcroFormField>
      );

    this.iframe.contentWindow?.document.head.insertAdjacentHTML(
      'beforeend',
      render(
        html`<style>
          @keyframes activeFieldOutlineFade {
            0% {
              outline: 4px solid #0969da;
            }
            99.9% {
              outline: 4px solid transparent;
            }
            100% {
              outline: unset;
            }
          }

          .active-field-outline {
            outline: 4px solid #0969da;
          }
          .active-field-outline--fade {
            animation: activeFieldOutlineFade 2s ease-out forwards;
          }
          .cumulus-mapped {
            --annotation-unfocused-field-background: url("data:image/svg+xml;charset=UTF-8,<svg width='1px' height='1px' xmlns='http://www.w3.org/2000/svg'><rect width='100%' height='100%' style='fill:rgba(46, 160, 67, 0.15);'/></svg>");
          }
        </style>`
      )
    );

    this.renderMappingFields(viewerApplication);
  }

  onCtrlS(_viewerApplication: PDFViewerApplication): void {
    this.form.requestSubmit();
  }

  onAnnotationFieldClick(
    viewerApplication: PDFViewerApplication,
    target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    event: Event
  ): void {
    this.handleAnnotationFieldClickOrChange(viewerApplication, target, event);
  }
  onAnnotationFieldFocus(
    viewerApplication: PDFViewerApplication,
    target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    event: Event
  ): void {
    this.handleAnnotationFieldClickOrChange(viewerApplication, target, event);
  }

  async handleAnnotationFieldClickOrChange(
    _viewerApplication: PDFViewerApplication,
    target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    _event: Event
  ) {
    const id = `acro-field-${target.dataset.elementId}`;
    const element = document.getElementById(id)!;
    clearTimeout(this.fieldOutlineTimeout);
    this.removeFieldOutline();
    await new Promise<void>(resolve => setTimeout(resolve, 0));
    element.classList.add('element-changed-animation');
    this.removeFieldOutline = () =>
      element.classList.remove('element-changed-animation');
    this.fieldOutlineTimeout = setTimeout(this.removeFieldOutline, 2050);
    scrollIntoViewIfNeeded(element);
    this.fixScrollTop('handleAnnotationClick - scrollIntoView');
  }

  onAnnotationFieldChange(
    viewerApplication: PDFViewerApplication,
    target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    _event: Event
  ): void {
    // sync all fields with the same name.
    const eventFieldId = target.getAttribute('data-element-id')!;
    const name = this.fieldsById[eventFieldId].name;
    const fields = this.acroForm[name].filter(field =>
      ['text', 'checkbox', 'radiobutton'].includes(field.type)
    );
    for (const field of fields) {
      const initialValue = field.value;
      const override = viewerApplication.pdfDocument.annotationStorage.getValue(
        field.id,
        {}
      );
      // console.log(name, id, initialValue, override, field);
      const value = override?.value ?? initialValue;
      const element = document.getElementById(
        `acro-field-value-${field.id}`
      ) as HTMLInputElement | HTMLTextAreaElement;
      element.value =
        typeof value === 'boolean' &&
        ['checkbox', 'radiobutton'].includes(field.type)
          ? value
            ? 'On'
            : 'Off'
          : String(value);
      dispatchChange(element);
      if (field.id === eventFieldId) {
        scrollIntoViewIfNeeded(element);
        this.fixScrollTop('handleAnnotationChange - scrollIntoView');
      }
    }
  }

  syncFieldMappedStatus(
    annotationInput: HTMLElement,
    expressionInput: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  ) {
    const value = expressionInput.value.trim();
    annotationInput.classList.toggle('cumulus-mapped', value !== '');
    annotationInput.title = value;
  }

  async syncAcroField(event: Event) {
    if (!this.viewerApplication) {
      return;
    }
    if (event.target instanceof HTMLAnchorElement) {
      event.preventDefault();
    }
    const viewerApplication = this.viewerApplication;
    const target = event.target as HTMLElement;
    const id = target.getAttribute('element-id')!;
    const field = this.fieldsById[id];

    // render page if not already rendered
    const page = viewerApplication.pdfViewer.getPageView(field.page!);
    viewerApplication.appConfig.toolbar.pageNumber.value = String(
      field.page! + 1
    );
    viewerApplication.appConfig.toolbar.pageNumber.dispatchEvent(
      new Event('change', { bubbles: true })
    );
    // viewerApplication.eventBus.dispatch('pagenumberchanged', {
    //   source: self,
    //   value: field.page! + 1
    // });
    if (page.renderingState !== 3 /* PDFRenderingStates.FINISHED */) {
      await Promise.all([
        new Promise<void>(resolve =>
          viewerApplication.eventBus.on('pagerendered', resolve, {
            once: true,
            signal: this.detachedController?.signal
          })
        ),
        new Promise<void>(resolve =>
          viewerApplication.eventBus.on('annotationlayerrendered', resolve, {
            once: true,
            signal: this.detachedController?.signal
          })
        )
      ]);
    }

    const element = viewerApplication.appConfig.viewerContainer.querySelector(
      `[data-element-id="${id}"]`
    ) as HTMLElement;

    if (event.type === 'change') {
      this.syncFieldMappedStatus(
        element,
        target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      );
    } else {
      // click (on anchor) or expression focus/blur

      // focus (which will scroll field into view) OR only scroll into view
      if (target instanceof HTMLAnchorElement) {
        element.focus();
        this.fixScrollTop('syncAcroField - focus');
      } else {
        scrollIntoViewIfNeeded(element);
        this.fixScrollTop('syncAcroField - scrollIntoView');
      }
      // remove previous outline
      clearTimeout(this.viewerOutlineTimeout);
      this.removeViewerOutline();
      this.removeViewerOutline = () => {};
      // outline current field
      if (event.type !== 'blur') {
        await new Promise<void>(resolve => setTimeout(resolve, 0));
        element.classList.add('active-field-outline');
        this.removeViewerOutline = () =>
          element.classList.remove(
            'active-field-outline',
            'active-field-outline--fade'
          );
      }
      // enqueue outline removal
      if (target instanceof HTMLAnchorElement) {
        element.classList.add('active-field-outline--fade');
        this.viewerOutlineTimeout = setTimeout(this.removeViewerOutline, 2050);
      }
    }
  }

  onAnnotationLayerRendered(viewerApplication: PDFViewerApplication): void {
    for (const { id } of Object.values(this.acroForm)
      .flat()
      .filter(x => ['text', 'checkbox', 'radiobutton'].includes(x.type))) {
      const annotationInput =
        viewerApplication.appConfig.viewerContainer.querySelector(
          `[data-element-id="${id}"]`
        ) as HTMLElement;
      if (!annotationInput) {
        continue;
      }
      const expressionInput = this.form.querySelector(
        `textarea[element-id="${id}"]`
      );
      if (!expressionInput) {
        continue;
      }
      this.syncFieldMappedStatus(
        annotationInput,
        expressionInput as
          | HTMLInputElement
          | HTMLTextAreaElement
          | HTMLSelectElement
      );
    }
  }

  renderMappingFields(viewerApplication: PDFViewerApplication) {
    const dataFields: PdfFieldMapping[] = JSON.parse(
      this.fieldsScript.textContent!
    );
    let templates: unknown[] = [];
    for (const name of this.getNamesInPositionOrder()) {
      const fields = this.acroForm[name].filter(field =>
        ['text', 'checkbox', 'radiobutton'].includes(field.type)
      );
      for (const field of fields) {
        const index = templates.length;
        const dataField = dataFields.find(
          dataField =>
            dataField.name === field.name &&
            dataField.export_values === field.exportValues
        );
        if (dataField && dataField.value !== field.value) {
          viewerApplication.pdfDocument.annotationStorage.setValue(field.id, {
            value: dataField.value
          });
        }
        templates.push(
          html`<section
            id="acro-field-${field.id}"
            class="rounded-1 p-3 mt-n3 ml-n3 mb-n3 mr-n3"
          >
            <h3 class="f4 mb-2">
              ${field.editable === false || field.hidden === true
                ? html`${breakAcroFieldName(field.name)}
                  ${field.type === 'radiobutton' && field.exportValues
                    ? `/ ${field.exportValues}`
                    : null}`
                : html`<a
                    class="Link--primary"
                    href="#"
                    tabindex="-1"
                    element-id="${field.id}"
                    data-action="click:pdf-template#syncAcroField"
                    >${breakAcroFieldName(field.name)}${field.type ===
                      'radiobutton' && field.exportValues
                      ? ` / ${field.exportValues}`
                      : null}</a
                  >`}
              <span class="Label Label--primary text-capitalize"
                >${field.type}</span
              >
              ${field.type === 'text' && field.charLimit
                ? html`<span class="Label">${field.charLimit} characters</span>`
                : null}
              ${field.type === 'text' && field.multiline
                ? html`<span class="Label">Multiline</span>`
                : null}
              ${field.editable === false
                ? html`<span class="Label Label--attention">Read only</span>`
                : null}
              ${field.hidden === true
                ? html`<span class="Label Label--attention">Hidden</span>`
                : null}
            </h3>
            ${formGroupTemplate({
              type: 'textarea',
              label: 'Mapping',
              value: dataField?.expression
                ? printExpression(dataField.expression.ast)
                : '',
              maxLength: 3000,
              disabled: this.titleInput.disabled,
              spellcheck: false,
              rows: 2,
              name: `mapping/fields/${index}/expression:nullstring`,
              inputAttrs: [attr('element-id', field.id)],
              action:
                'focus:pdf-template#syncAcroField blur:pdf-template#syncAcroField change:pdf-template#syncAcroField',
              classes: 'position-relative',
              headerClasses: 'sr-only'
            })}
            <details class="details-reset">
              <summary class="note" tabindex="-1">Details</summary>
              <div class="d-flex flex-column gap-3 mt-2 ml-3">
                ${formGroupTemplate({
                  type: field.multiline === true ? 'textarea' : 'text',
                  label: 'Value',
                  value: dataField ? dataField.value : field.value,
                  maxLength:
                    field.type === 'text' && field.charLimit
                      ? field.charLimit
                      : 300,
                  name: `mapping/fields/${index}/value`,
                  disabled: true,
                  id: `acro-field-value-${field.id}`
                })}
                <pre><code class="p-3 f6 overflow-auto">${JSON.stringify(
                  field,
                  null,
                  2
                )}</code></pre>
              </div>
            </details>
            <input
              type="hidden"
              name="mapping/fields/${index}/name"
              value="${field.name}"
            />
            <input
              type="hidden"
              name="mapping/fields/${index}/type"
              value="${field.type}"
            />
            <input
              type="hidden"
              name="mapping/fields/${index}/id"
              value="${field.id}"
            />
            <input
              type="hidden"
              name="mapping/fields/${index}/page:integer"
              value="${field.page}"
            />
            ${field.editable === undefined
              ? null
              : html`<input
                  type="hidden"
                  name="mapping/fields/${index}/editable:boolean"
                  value="${field.editable}"
                />`}
            ${field.hidden === undefined
              ? null
              : html`<input
                  type="hidden"
                  name="mapping/fields/${index}/hidden:boolean"
                  value="${field.hidden}"
                />`}
            ${field.multiline === undefined
              ? null
              : html`<input
                  type="hidden"
                  name="mapping/fields/${index}/multiline:boolean"
                  value="${field.multiline}"
                />`}
            ${field.charLimit === undefined
              ? null
              : html`<input
                  type="hidden"
                  name="mapping/fields/${index}/charlimit:integer"
                  value="${field.charLimit}"
                />`}
            ${field.exportValues === undefined
              ? null
              : html`<input
                  type="hidden"
                  name="mapping/fields/${index}/export_values"
                  value="${field.exportValues}"
                />`}
            ${field.defaultValue === undefined
              ? null
              : html`<input
                  type="hidden"
                  name="mapping/fields/${index}/default_value"
                  value="${field.defaultValue}"
                />`}
          </section>`
        );
      }
    }
    this.form.insertAdjacentHTML('beforeend', render(templates));
    (this.form.firstElementChild as FormBehaviorElement).acceptChanges();
  }

  fixScrollTop = (message: string) => {
    if (document.documentElement.scrollTop > 0) {
      document.documentElement.scrollTop = 0;
      console.log('fixScrollTop', message);
    }
    setTimeout(this.fixScrollTop, 0, message + ' + timeout');
  };

  getNamesInPositionOrder() {
    return Object.values(this.acroForm)
      .flat()
      .filter(widget =>
        ['text', 'checkbox', 'radiobutton'].includes(widget.type)
      )
      .map(widget => ({
        page: widget.page ?? 1000, // fallback if page is missing
        name: widget.name,
        rect: widget.rect ?? [0, 0] // fallback if rect is missing
      }))
      .sort((a, b) => {
        // sort by page ascending, then Y descending (top to bottom), then X ascending (left to right)
        const dp = a.page - b.page;
        const dy = b.rect[1] - a.rect[1];
        return dp !== 0 ? dp : dy !== 0 ? dy : a.rect[0] - b.rect[0];
      })
      .map(widget => widget.name)
      .filter((x, i, a) => a.indexOf(x) === i); // remove duplicates
  }
}

@controller('select-pdf-templates')
export class SelectPdfTemplatesElement extends HTMLElement {
  @target declare form: HTMLFormElement;

  selectTemplates(event: Event) {
    const target = event.target as HTMLButtonElement;
    const selectAll = target.dataset.select === 'all';
    const checkboxes = this.form.elements.namedItem(
      'template'
    ) as RadioNodeList;
    for (const checkbox of checkboxes) {
      if (!(checkbox instanceof HTMLInputElement)) {
        throw new Error('Expected HTMLInputElement');
      }
      checkbox.checked = selectAll && !checkbox.disabled;
      dispatchChange(checkbox);
    }
  }

  filterTemplates(event: Event) {
    const target = event.target as HTMLButtonElement;
    const label = target.dataset.filterLabel!.toLowerCase();
    target.parentElement?.querySelectorAll('[aria-current]').forEach(el => {
      el.removeAttribute('aria-current');
    });
    target.setAttribute('aria-current', 'true');
    const checkboxes = this.form.elements.namedItem(
      'template'
    ) as RadioNodeList;
    for (const checkbox of checkboxes) {
      if (!(checkbox instanceof HTMLInputElement)) {
        throw new Error('Expected HTMLInputElement');
      }
      const visible =
        label === 'all' ||
        checkbox.dataset.labels?.toLowerCase().split(',').includes(label);
      if (!visible && checkbox.checked) {
        checkbox.checked = false;
        dispatchChange(checkbox);
      }
      checkbox.closest('label')?.toggleAttribute('hidden', !visible);
    }
  }
}

@controller('pdf-viewer')
export class PdfViewerElement extends PDFViewerBase {
  @target declare valuesScript: HTMLScriptElement;
  preparedPromise = Promise.withResolvers<PDFViewerApplication>();

  onAnnotationFieldClick: undefined;
  onAnnotationFieldFocus: undefined;
  onCtrlS: undefined;
  onAnnotationLayerRendered: undefined;

  onAnnotationFieldChange() {
    this.dispatchEvent(
      new Event('pdf-viewer-field-changed', { bubbles: true })
    );
  }

  async onPdfLoaded(viewerApplication: PDFViewerApplication) {
    const values: Pick<PdfFieldMapping, 'name' | 'export_values' | 'value'>[] =
      JSON.parse(this.valuesScript.textContent!);
    const acroForm = await viewerApplication.pdfDocument.getFieldObjects();
    if (!acroForm) {
      console.warn('No acroForm found');
      this.preparedPromise.resolve(viewerApplication);
      return;
    }
    const fields = Object.values(acroForm).flat();
    for (const { name, export_values, value } of values) {
      const field = fields.find(
        field => field.name === name && field.exportValues === export_values
      );
      if (!field) {
        console.warn(
          `Field ${name} with export value ${export_values} not found`
        );
        continue;
      }
      if (field.value === value) {
        continue;
      }
      viewerApplication.pdfDocument.annotationStorage.setValue(field.id, {
        value
      });
    }
    this.preparedPromise.resolve(viewerApplication);
  }

  async save(): Promise<Uint8Array<ArrayBufferLike>> {
    // https://github.com/mozilla/pdf.js/blob/2e10ff6dd4a875b63743b4a764e7375f1bb94e1d/web/app.js#L1174
    // https://github.com/mozilla/pdf.js/blob/master/web/download_manager.js#L27
    const viewerApplication = await this.preparedPromise.promise;
    const downloadManager = viewerApplication.downloadManager;
    let data: Uint8Array<ArrayBufferLike>;
    viewerApplication.downloadManager = {
      download(d) {
        data = d;
      }
    };
    try {
      await viewerApplication.downloadOrSave();
    } finally {
      viewerApplication.downloadManager = downloadManager;
    }
    return Uint8Array.from(data!);
  }
}

declare const PDFLib: typeof import('pdf-lib');

@controller('prepare-pdf-documents')
export class PreparePdfDocumentsElement extends HTMLElement {
  @targets declare viewerElements: PdfViewerElement[];
  @targets declare instructionElements: HTMLElement[];
  @target declare nextButton: HTMLButtonElement;
  @target declare previousButton: HTMLButtonElement;
  @target declare downloadForm: HTMLFormElement;
  @target declare templatesScript: HTMLScriptElement;
  current = 0;
  hasChanges = false;
  isSubmitting = false;

  selectTemplate(event: Event) {
    const index = +(event.target as HTMLSelectElement).value;
    if (index === this.current) {
      return;
    }
    this.viewerElements[this.current].classList.add('v-hidden');
    this.instructionElements[this.current].hidden = true;
    this.current = index;
    this.viewerElements[this.current].classList.remove('v-hidden');
    this.instructionElements[this.current].hidden = false;
  }

  // showPrevious() {
  //   this.viewerElements[this.current].classList.add('v-hidden');
  //   this.instructionElements[this.current].hidden = true;
  //   this.current--;
  //   this.viewerElements[this.current].classList.remove('v-hidden');
  //   this.instructionElements[this.current].hidden = false;
  //   this.previousButton.disabled = this.current === 0;
  //   this.nextButton.disabled = this.current === this.viewerElements.length - 1;
  // }

  // showNext() {
  //   this.viewerElements[this.current].classList.add('v-hidden');
  //   this.instructionElements[this.current].hidden = true;
  //   this.current++;
  //   this.viewerElements[this.current].classList.remove('v-hidden');
  //   this.instructionElements[this.current].hidden = false;
  //   this.previousButton.disabled = this.current === 0;
  //   this.nextButton.disabled = this.current === this.viewerElements.length - 1;
  // }

  async download(event: CustomEvent<FormData>) {
    event.preventDefault();
    if (this.isSubmitting) {
      return;
    }
    try {
      this.isSubmitting = true;
      const pdfTemplates = JSON.parse(
        this.templatesScript.textContent!
      ) as Pick<
        ActivityDefinitionPdfTemplate,
        'title' | 'start_page' | 'end_page'
      >[];
      await Promise.resolve();
      busySubmitButtons(this.downloadForm, true);
      let { filename, type: downloadType } = Object.fromEntries(
        event.detail
      ) as { filename: string; type?: string };
      const pdfs = await Promise.all(this.viewerElements.map(el => el.save()));
      let url: string;
      if (downloadType === 'zip') {
        if (!/\.zip$/i.test(filename)) {
          filename += '.zip';
        }

        const data = await zipFiles(
          pdfs.map((data, i) => ({
            filename: sanitizeFilename(pdfTemplates[i].title + '.pdf'),
            data
          })),
          'Created by ' +
            this.getAttribute('user_name')! +
            ' at ' +
            this.getAttribute('organization_name')!
        );
        url = URL.createObjectURL(
          new Blob([data], { type: 'application/zip' })
        );
      } else {
        if (!/\.pdf$/i.test(filename)) {
          filename += '.pdf';
        }
        const data = await mergePdfs({
          title: filename,
          author:
            this.getAttribute('user_name')! +
            ' at ' +
            this.getAttribute('organization_name')!,
          pdfs: pdfs.map((data, i) => ({
            title: pdfTemplates[i].title,
            data,
            start_page: pdfTemplates[i].start_page,
            end_page: pdfTemplates[i].end_page
          }))
        });
        url = URL.createObjectURL(
          new Blob([data], { type: 'application/pdf' })
        );
      }
      console.log(filename);
      download(url, sanitizeFilename(filename));
    } finally {
      busySubmitButtons(this.downloadForm, false);
      this.isSubmitting = false;
    }
  }

  pdfFieldChanged() {
    if (this.hasChanges) {
      return;
    }
    this.hasChanges = true;
    window.addEventListener('beforeunload', event => {
      event.preventDefault();
      event.returnValue = 'You have unsaved work.'; // browser overrides this text
    });
  }
}

function download(blobUrl: string, filename: string) {
  const a = document.createElement('a');
  if (!a.click) {
    throw new Error('DownloadManager: "a.click()" is not supported.');
  }
  a.href = blobUrl;
  a.target = '_parent';
  // Use a.download if available. This increases the likelihood that
  // the file is downloaded instead of opened by another PDF plugin.
  if ('download' in a) {
    a.download = filename;
  }
  // <a> must be in the document for recent Firefox versions,
  // otherwise .click() is ignored.
  (document.body || document.documentElement).append(a);
  a.click();
  a.remove();
}

/*
Issues:
- https://github.com/mozilla/pdf.js/issues/19837

template list
- feature flag

template detail
- feature flag

activity ui
- feature flag
- download zip
- auto-reload
*/
